import type { DatasetNaming } from '@/entities/dataset';

/* What the notification centre says, decided here rather than in the bell.
 *
 * **There is no notification API, and this file is the reason that is not a problem.** The platform
 * has no `notification` table, no per-user read state and no feed endpoint; what it has is three
 * reads other screens already make — an open transition request per draft rule, the incident queue,
 * and the PII review queue. A feed is those three reduced to one ordered list, and reducing them in a
 * model rather than in JSX is what makes the two properties below testable without a browser.
 *
 * The two properties, and they are the whole point:
 *
 * * **Nothing here is a fabricated count.** A source that could not be read never contributes a zero.
 *   `badgeFor` reports the count it can see *and* that the answer is partial, because a bell with no
 *   badge is a claim that there is nothing to do — the strongest claim this component makes, on the
 *   least evidence. The distinction is three-way rather than two: a source the caller is not
 *   *permitted* to read is a known nothing, and reporting that as a blind spot would put a permanent
 *   warning on the header of anybody holding a role that withholds one of the three reads — which
 *   `data-steward` did for most of this platform's life, its reads being hand-listed where
 *   `READER`'s are derived.
 *
 * * **A row appears only for somebody who can act on it.** For an approval that means holding
 *   `activate:rules.rule` *or* being the requester, and the two are different rows: `ApprovalService`
 *   refuses the requester's own signature by identity, so a requester is told their request is
 *   waiting and is never invited to sign it. See `approvalIsSignable`.
 */

/* ---------------------------------------------------------------- the shape of a row */

export type NotificationKind =
  'rule-approval' | 'remediation-approval' | 'incident' | 'review';

/** Semantic, never a colour. The bell maps a tone to a token; a tone is what this model decides. */
export type NotificationTone = 'danger' | 'warn' | 'neutral';

export interface Notification {
  /** Stable identity, and **also the dismissal subject** — see `dismissalOf` for what goes in it. */
  readonly id: string;
  readonly kind: NotificationKind;
  /** The ICU key for what happened. A key, never a sentence: no English in a `.ts`. */
  readonly messageKey: string;
  /** The arguments that key takes. Numbers only, so this model states no prose of its own. */
  readonly messageArgs: Readonly<Record<string, number>>;
  /** Which thing it is about: a resolved name, or the key of a sentence saying why there is none.
   *
   * `DatasetNaming` from `entities/dataset` rather than a local pair, because it is the same
   * question three other screens answer and the bare-UUID defect is recorded five times in this
   * codebase's own history. A rule's name is always known — it came from the listing that found the
   * request — so an approval row is always `named`. */
  readonly subject: DatasetNaming;
  /** Where the reader goes to act on it. */
  readonly to: string;
  /** When it happened, as the source's own instant. **`null` rather than a fabricated "now"** for a
   * source that carries none: the review queue is a depth, not an event, and stamping it with the
   * time of the fetch would make an untouched queue look like it just arrived. */
  readonly at: string | null;
  readonly tone: NotificationTone;
  /** Whether the caller can take the action, or is only being told it is waiting. Rendered as a
   * distinction rather than folded away — an invitation to do something the server refuses is worse
   * than no invitation. */
  readonly actionable: boolean;
}

/* ---------------------------------------------------------------- approval requests */

/** What an open transition request is *waiting for*, which is not the same as its stored state.
 *
 * `unrecognised` is the fifth member and it is deliberate. `TransitionApproval.state` is a closed
 * enum in the contract and a `string` on the wire, and a `default:` that quietly returned `spent`
 * would make a state this build has not heard of disappear from the feed — the silent-quietest-branch
 * failure this repository records on a `default:`. An unknown state is a thing somebody should look
 * at, so it surfaces as a row that says so.
 */
export type ApprovalPhase = 'awaiting' | 'ready' | 'rejected' | 'spent' | 'unrecognised';

export interface ApprovalRequestLike {
  readonly state: string;
  readonly satisfied: boolean;
  readonly requester: string;
}

export function approvalPhase(request: ApprovalRequestLike): ApprovalPhase {
  switch (request.state) {
    case 'pending':
      /* `satisfied` wins over `pending`, and the pair is not redundant: the second signature
       * satisfies the approval and the *rule* is still a draft until somebody activates it. So a
       * satisfied request is work waiting for a person, not a finished thing. */
      return request.satisfied ? 'ready' : 'awaiting';
    case 'approved':
      return 'ready';
    case 'rejected':
      return 'rejected';
    /* Widening the approver pool after an SLA breach. Still one signature short of satisfied — see
     * `ApprovalState`, which has no `expired` precisely so a timeout can never reach `approved`. */
    case 'escalated':
      return 'awaiting';
    /* The approval was spent on the transition it authorised (migration 0042). Nothing is waiting. */
    case 'consumed':
      return 'spent';
    default:
      return 'unrecognised';
  }
}

/** The caller's standing on one request. Two independent facts, and both are needed. */
export interface Standing {
  /** Holds `activate:rules.rule`. Necessary for a signature and not sufficient — see below. */
  readonly maySign: boolean;
  /** Is the person who raised this request. */
  readonly isRequester: boolean;
  /** **Has already recorded a decision on it**, which is the second half of the identity check and
   * was missing.
   *
   * `ApprovalService` refuses a *second* decision from the same approver — `AlreadyDecidedError`,
   * 409 — for the same reason it refuses the requester's own: two signatures must be two people.
   * Modelling only `isRequester` therefore invited an approver who had already signed to sign
   * again, and the row said "you can act on this" about a control that answers 409. Seen in the
   * browser on a real request: the approver who had supplied the one collected signature was told
   * the remaining one was hers to give.
   *
   * The client can know this without another read — `TransitionApproval.decisions[]` carries an
   * `approver` per decision, which is the same payload the row's count comes from. */
  readonly hasDecided: boolean;
}

/** Whether this request belongs in this caller's feed at all.
 *
 * **The panel is titled "Waiting for you", so an item that is not waiting for the reader does not
 * belong in it.** This used to be `maySign || isRequester`, which put an author's own outstanding
 * request in their bell — where it rendered, correctly, as "waiting on someone else". A row that
 * says so inside a panel that says the opposite is the panel contradicting itself, and worse it was
 * *counted*: the badge read 2 when one of the two was something the reader could do nothing about.
 * A count of work you cannot do is how a badge stops being read.
 *
 * So an `awaiting` request is shown to the people who can sign it and not to its author. What the
 * author does get is the half that genuinely is theirs: `ready` — the second signature arrived and
 * the rule still needs activating — which `approvalIsActionable` admits for them, and `rejected`,
 * which is their problem and nobody else's. The status of a request they raised is on the rule's own
 * page, where they raised it; the bell is a work queue rather than a status feed, and conflating the
 * two is what made the badge overstate.
 *
 * A reader who can neither sign nor is the author has nothing to do with it, and listing it would
 * make the badge a count of other people's work. A **rejected** request is narrower still: it is the
 * requester's problem and nobody else's, so a co-approver is not told that a colleague declined
 * something they were not involved in.
 */
export function approvalIsVisible(phase: ApprovalPhase, standing: Standing): boolean {
  if (phase === 'spent') return false;
  if (phase === 'rejected') return standing.isRequester;
  /* **`unrecognised` is shown to anybody involved, and deliberately does not go through
   * `approvalIsActionable`.** It means this build does not understand the state the server sent, so
   * whether the reader can act on it is precisely the thing we cannot work out — and answering
   * "no, hide it" would be the silent-quietest-branch failure the test beside it exists to catch.
   * Routing it through actionability made it vanish from every feed; the test caught that. */
  if (phase === 'unrecognised') return standing.maySign || standing.isRequester;
  /* Everything else is visible exactly when the reader can act on it. See the note above for why
   * the requester is no longer included on `awaiting`. */
  return approvalIsActionable(phase, standing);
}

/** Whether the row may invite a signature.
 *
 * **The permission is not the rule.** `ApprovalService` refuses the requester's own signature
 * whoever they are and whatever they hold — the check is on identity, so an admin holding
 * `activate:rules.rule` can sign anybody's request and not their own. A row that offered it anyway
 * would be a control that answers 403, and the reader would be left guessing whether they lacked a
 * permission or were the requester. `TransitionApproval.tsx` states the same mechanism for the rule's
 * own page; this is the header agreeing with it rather than a second opinion.
 *
 * `ready` is not signable either: the remaining step is the activation, not another signature.
 */
export function approvalIsSignable(phase: ApprovalPhase, standing: Standing): boolean {
  return (
    phase === 'awaiting' &&
    standing.maySign &&
    !standing.isRequester &&
    /* Both identity exclusions, not one. See `Standing.hasDecided`. */
    !standing.hasDecided
  );
}

/* The ICU key per phase, and the phase is also in the dismissal id — see `approvalNotification`. */
const APPROVAL_MESSAGE: Readonly<Record<ApprovalPhase, string>> = {
  awaiting: 'notifications.approval.awaiting',
  ready: 'notifications.approval.ready',
  rejected: 'notifications.approval.rejected',
  /* Never rendered — `approvalIsVisible` refuses it — and mapped anyway, because a total map is what
   * makes a sixth phase a compile error here rather than a row with a missing key rendering as the
   * key itself. */
  spent: 'notifications.approval.spent',
  unrecognised: 'notifications.approval.unrecognised',
};

const APPROVAL_TONE: Readonly<Record<ApprovalPhase, NotificationTone>> = {
  /* Amber, not red. A rule waiting for a second signature is blocked on a person who has been
   * asked; nothing is deteriorating while it waits, which is the reason the dashboard's worklist
   * ranks approvals last of its four sources. */
  awaiting: 'warn',
  ready: 'neutral',
  rejected: 'warn',
  spent: 'neutral',
  /* A state we cannot interpret is not a fault of the data. Neutral and *present* is the honest
   * pair: the row exists so somebody looks, and colouring it red would page them. */
  unrecognised: 'neutral',
};

export interface ApprovalInput {
  readonly ruleId: string;
  readonly ruleName: string;
  readonly ruleHref: string;
  readonly request: ApprovalRequestLike & {
    readonly required_eyes: number;
    readonly eyes_collected: number;
    readonly submitted_at: string;
  };
}

/** One approval request as a row, or `null` when it is not this caller's business.
 *
 * The counts travel as arguments rather than as a formatted string because `0 of 2 signatures` is
 * the figure the activation refusal is about, and it is the figure an approver checks before
 * signing — so it belongs on the row and it belongs in the catalogue where a translator can reach it.
 */
export function approvalNotification(
  input: ApprovalInput,
  standing: Standing,
): Notification | null {
  const phase = approvalPhase(input.request);
  if (!approvalIsVisible(phase, standing)) return null;

  return {
    /* **The phase is in the id, and that is a decision about dismissal rather than about identity.**
     * A request that was awaiting a signature and is now ready to activate is a *different fact*, so
     * dismissing the first must not silence the second. Keying on the request id alone would mean an
     * approver who cleared the "waiting" row never hears that it became unblocked. */
    id: `rule-approval:${input.ruleId}:${phase}`,
    kind: 'rule-approval',
    messageKey: APPROVAL_MESSAGE[phase],
    messageArgs: {
      collected: input.request.eyes_collected,
      required: input.request.required_eyes,
    },
    /* Always `named`: the rule's name came from the same listing that found the request, so there is
     * no gap to explain. */
    subject: { kind: 'named', name: input.ruleName },
    to: input.ruleHref,
    at: input.request.submitted_at,
    tone: APPROVAL_TONE[phase],
    actionable: approvalIsActionable(phase, standing),
  };
}

/** Whether *this* reader can take the step the row names.
 *
 * **Not `approvalIsSignable`, and collapsing the two produced a row that contradicted itself.** A
 * `ready` request rendered the headline *"approval is satisfied and the rule is still a draft —
 * activate it"* above the sub-label *"waiting on someone else"*: one half addressed to the reader,
 * the other telling them it was not theirs to do. Seen on a real request in the browser, not in a
 * test.
 *
 * The distinction the model had lost is the one the whole two-eyes mechanism turns on: **the
 * identity check is on the signature, never on the activation.** `ApprovalService` refuses the
 * requester's own *signature* — that is what stops one person doing both halves — and nothing stops
 * the requester performing the transition once two other people have signed it. So:
 *
 * * `awaiting` — the step is a signature, and the requester is excluded by identity.
 * * `ready` — the step is the activation, and `activate:rules.rule` is the whole of the
 *   requirement. A requester holding it may act, and telling them otherwise would leave a satisfied
 *   approval sitting on a draft rule with everybody believing it was somebody else's move.
 *
 * `maySign` names the permission rather than the act, which is accurate: activating and signing are
 * governed by the same `activate:rules.rule`, which is why one flag serves both branches.
 */
export function approvalIsActionable(phase: ApprovalPhase, standing: Standing): boolean {
  if (phase === 'ready') return standing.maySign;
  return approvalIsSignable(phase, standing);
}

/* ---------------------------------------------------------------- incidents */

export interface IncidentInput {
  readonly id: string;
  readonly severity: string;
  readonly openedAt: string;
  /** Already resolved by the caller through `nameDataset`, because the incident-kind branch and the
   * catalogue map are both the caller's to hold. */
  readonly subject: DatasetNaming;
  readonly href: string;
}

/** An open incident as a row. `info` is excluded by the caller, not here — see the bell.
 *
 * The tone mirrors `attentionTone` on the dashboard deliberately: two surfaces counting the same
 * queue must not disagree about which severities are loud.
 */
export function incidentNotification(input: IncidentInput): Notification {
  return {
    /* No phase suffix, unlike an approval. An incident's *state* changes (open → acknowledged) and
     * both mean the condition still holds — `isActionable` reads `active` for exactly that reason —
     * so a reader who dismissed it has not been told anything new by the acknowledgement. */
    id: `incident:${input.id}`,
    kind: 'incident',
    messageKey:
      input.severity === 'critical'
        ? 'notifications.incident.critical'
        : 'notifications.incident.warning',
    messageArgs: {},
    subject: input.subject,
    to: input.href,
    at: input.openedAt,
    tone: input.severity === 'critical' ? 'danger' : 'warn',
    /* A row that leads somewhere a person acts. Acknowledging is a decision with an actor recorded
     * against it (migration 0032), so it happens on the queue where the reason and the handover are
     * visible — not from the app chrome. */
    actionable: true,
  };
}

/* ---------------------------------------------------------------- remediation approvals */

export interface RemediationApprovalInput {
  readonly planId: string;
  /** The table the plan is about, or the key of a sentence saying it no longer names one — the same
   * `DatasetNaming` every other row carries, so the bell renders one thing. */
  readonly subject: DatasetNaming;
  readonly submittedAt: string;
  readonly href: string;
}

/** A remediation plan waiting on *this* reader's signature.

 * **Only rows the reader can actually sign reach this function**, and that is the whole design. The
 * caller filters on `decision_refusal === null` — the server's own answer, computed by the same code
 * that enforces the refusal — so this is never a notification about somebody else's decision.
 *
 * That filter is why there is no `Standing` argument here, unlike `approvalNotification`. The rules
 * half has to work out signability on the client, from a requester and a decision list it happens to
 * receive; remediation asks the server, which is the only party that knows the delegation chain. One
 * fewer place for a client to have its own opinion about who may approve.
 *
 * `actionable` is therefore always true. A plan the reader cannot sign is not a quieter row — it is
 * not their business, and the bell would be telling them about a queue they cannot clear.
 */
export function remediationApprovalNotification(
  input: RemediationApprovalInput,
): Notification {
  return {
    /* No phase suffix. A remediation plan leaves `awaiting_approval` the moment it is decided, so a
     * row that is still here has not changed — where a rule request can sit in `awaiting` while
     * collecting signatures, which is why that one keys on its phase. */
    id: `remediation-approval:${input.planId}`,
    kind: 'remediation-approval',
    messageKey: 'notifications.remediation.awaiting',
    messageArgs: {},
    subject: input.subject,
    to: input.href,
    at: input.submittedAt,
    /* `warn`, not `danger`. Nothing is broken and nothing is degrading — somebody is waiting, which
     * is the same weight as a warning incident and deliberately less than a critical one. */
    tone: 'warn',
    actionable: true,
  };
}

/* ---------------------------------------------------------------- the PII review queue */

/** The review queue as **one** row rather than one per dataset.
 *
 * A feed is a set of decisions, and "review 40 classifications" is one decision a steward makes
 * once; forty rows would bury every incident under a single afternoon's work. The dashboard's
 * worklist reaches the same conclusion for the same reason.
 *
 * **The depth is in the dismissal id, and that is the interesting half.** A queue has no event to
 * key on, so dismissing "40 waiting" and then being silent at 60 would be a notification that goes
 * quiet as the backlog grows — the exact inversion of what it is for. Keying on the depth means a
 * dismissed steady state stays quiet and a growing one speaks up again.
 */
export function reviewNotification(depth: number, href: string): Notification | null {
  if (depth === 0) return null;
  return {
    id: `review:${String(depth)}`,
    kind: 'review',
    messageKey: 'notifications.review.waiting',
    messageArgs: { count: depth },
    /* The subject is the queue, not a dataset, so there is no id to name and no gap to explain. The
     * count is in the message; this says which queue. */
    subject: { kind: 'gap', reasonKey: 'notifications.review.subject' },
    to: href,
    /* No instant. A queue depth is a standing figure rather than something that happened at a
     * moment, and `orderNotifications` puts it after everything that carries a real time. */
    at: null,
    tone: 'neutral',
    actionable: true,
  };
}

/* ---------------------------------------------------------------- ordering */

/** Newest first, with the timeless rows after them and a stable tiebreak.
 *
 * `at === null` sorts **last** rather than first or interleaved: it means "this has no moment", and
 * treating an absent instant as the epoch would bury a standing queue under nothing, while treating
 * it as now would put it above a critical incident that opened an hour ago. Last is the only reading
 * that claims nothing.
 *
 * The `id` tiebreak is not cosmetic. Two rows with the same instant — a fused execution's incidents
 * share one — must not swap places between renders, because a list whose order changes under the
 * reader is one they cannot work down. `Array.prototype.sort` is stable per spec, and the explicit
 * comparison is what makes it stable across *sources*, where the input order is whichever of three
 * requests resolved first.
 */
export function orderNotifications(
  items: readonly Notification[],
): readonly Notification[] {
  return [...items].sort((a, b) => {
    if (a.at === null && b.at === null) return a.id.localeCompare(b.id);
    if (a.at === null) return 1;
    if (b.at === null) return -1;
    /* String comparison on ISO-8601 instants, which is correct for the UTC form the API sends and is
     * what `orderForQueue` already does. `Date.parse` here would turn an unparseable timestamp into
     * a `NaN` comparison, and a comparator that returns `NaN` produces an implementation-defined
     * order — a silently shuffled list rather than a visible failure. */
    return b.at.localeCompare(a.at) || a.id.localeCompare(b.id);
  });
}

/* ---------------------------------------------------------------- what "unread" means here */

/** Where dismissals live.
 *
 * **`localStorage`, and the panel says so in words.** There is no server-side read state and this
 * does not invent one: a `notification_read` table would be a new persisted fact about a person, and
 * the honest cheap alternative is a per-device preference — which is what the theme toggle and the
 * sidebar's collapse state already are. The cost is real and is stated on screen rather than left to
 * be discovered: dismiss something on a laptop and it is still there on a phone.
 */
export const DISMISSED_KEY = 'lumiq.notifications.dismissed';

/** How many dismissals to keep. A bound, because the store is written by a browser and never pruned
 * by a server: without one, a long-lived session accumulates a dismissal per incident for ever and
 * eventually trips the storage quota, which throws on write and would make the *toggle* fail. */
export const MAX_DISMISSED = 500;

export function readDismissed(): ReadonlySet<string> {
  try {
    const raw = window.localStorage.getItem(DISMISSED_KEY);
    if (raw === null) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id): id is string => typeof id === 'string'));
  } catch {
    /* Storage denied, or a value some other tool wrote. Nothing dismissed is the safe default: it
     * shows a row again, where the failure in the other direction hides one. */
    return new Set();
  }
}

export function writeDismissed(ids: readonly string[]): void {
  try {
    window.localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids.slice(-MAX_DISMISSED)));
  } catch {
    /* A preference that cannot be saved is not worth failing a render over — the dismissal still
     * holds for this session, exactly as `ThemeToggle` accepts for the theme. */
  }
}

/** Drop dismissals for rows that no longer exist, keeping the newest.
 *
 * Two jobs, and the first is the one that matters. An incident that was dismissed and has since been
 * resolved will never reappear, so its id is dead weight — and because an approval's id carries its
 * phase, a request that moves on leaves one behind on every transition. Without pruning the store
 * grows monotonically in a browser nobody clears.
 *
 * `live` is the set of ids *currently in the feed*, so this is deliberately called with a settled
 * feed only: pruning while a source is still loading would forget a dismissal for a row that is
 * about to come back, and it would come back undismissed.
 */
export function pruneDismissed(
  dismissed: ReadonlySet<string>,
  live: ReadonlySet<string>,
): readonly string[] {
  return [...dismissed].filter((id) => live.has(id)).slice(-MAX_DISMISSED);
}

export function unreadCount(
  items: readonly Notification[],
  dismissed: ReadonlySet<string>,
): number {
  return items.filter((item) => !dismissed.has(item.id)).length;
}

/* ---------------------------------------------------------------- how complete the answer is */

/** One source's state, and there are four because there are four different facts.
 *
 * `forbidden` is the member that earns this type. Folding it into `unreadable` would report an
 * ordinary permission boundary as a fault — a `data-steward` holds no `observability.incident:read`
 * at all, so the header of the role that does most of the work in this product would carry a
 * permanent "we could not read this". Folding it into `ok` would be worse: it would let a caller
 * conclude that nothing is waiting in a queue nobody asked about.
 */
export type SourceState = 'reading' | 'ok' | 'unreadable' | 'forbidden';

export type NotificationSource = 'approvals' | 'remediation' | 'incidents' | 'review';

/** What the caller is allowed to know about a source, decided before any request is made.
 *
 * **`unknown` is the member that fixes a real defect of mine.** `can` denies while the identity read
 * is in flight — correctly, so a control cannot appear and then vanish — and the first version of
 * this feed read that denial as `forbidden`. `forbidden` is not partial, so for the length of the
 * identity read the bell claimed *"nothing is waiting for you"*: the strongest claim it makes, on no
 * evidence at all, in the one window a reader is most likely to be looking at a freshly loaded page.
 *
 * `unreadable` is the fourth, for an identity read that *failed*. Without it the feed would sit on
 * `unknown` for ever and the badge would show a loading dot that never resolves — a spinner standing
 * in for an answer, which is the shape this codebase records as worse than an error.
 */
export type Access = 'unknown' | 'unreadable' | 'permitted' | 'forbidden';

/** Fold what the caller may know and what the request did into one state.
 *
 * The order is the whole content of this function: an access answer settles the question before a
 * query's own state is consulted, because a query that was never issued is `isPending` for ever and
 * would otherwise report a permission boundary as "still reading".
 */
export function sourceStateFor(
  access: Access,
  query: { readonly isError: boolean; readonly isPending: boolean },
): SourceState {
  if (access === 'unknown') return 'reading';
  if (access === 'unreadable') return 'unreadable';
  if (access === 'forbidden') return 'forbidden';
  if (query.isError) return 'unreadable';
  if (query.isPending) return 'reading';
  return 'ok';
}

export interface SourceReport {
  readonly source: NotificationSource;
  readonly state: SourceState;
  /** Rows the feed knows it did not look at — the approval fan-out's bound, reported rather than
   * truncated silently. `PageMeta.truncated` and `DatasetNames.truncated` are the precedents. */
  readonly notLookedAt: number;
  /** Lookups that were attempted and failed. Distinct from `notLookedAt` because they are different
   * sentences: one is "we stopped", the other is "we asked and could not get an answer". */
  readonly failed: number;
}

/** Whether the count on the badge could be lower than the truth.
 *
 * **`forbidden` is not partial and `reading` is**, which is the whole judgement in this function. A
 * source the caller may not read contributes a *known* nothing; a source still in flight contributes
 * an unknown, and a badge that claimed completeness for two seconds and then changed would be wrong
 * for exactly as long as somebody was looking at it.
 */
export function isPartial(reports: readonly SourceReport[]): boolean {
  return reports.some(
    (report) =>
      report.state === 'unreadable' ||
      report.state === 'reading' ||
      report.notLookedAt > 0 ||
      report.failed > 0,
  );
}

/** Reports worth telling the reader about, in a stable order. `ok` sources say nothing. */
export function blindSpots(reports: readonly SourceReport[]): readonly SourceReport[] {
  return reports.filter(
    (report) => report.state !== 'ok' || report.notLookedAt > 0 || report.failed > 0,
  );
}

/** What the bell paints.
 *
 * Three shapes rather than a number, because "nothing is waiting", "we do not know yet" and "four,
 * and possibly more" are three different things to tell somebody — and only the first of them is
 * allowed to render no badge at all. An absent badge is the strongest claim this component makes.
 */
export type Badge =
  | { readonly kind: 'none' }
  | { readonly kind: 'reading' }
  | { readonly kind: 'count'; readonly count: number; readonly partial: boolean };

/* The copy keys for a blind spot, as total maps rather than a template literal at the call site.
 *
 * A key built with `` `notifications.blind.${state}` `` compiles, passes the linter, and renders as
 * the key itself when it is missing — the defect the sidebar shipped once, reading "nav.platform" in
 * production with all four gates green. A `satisfies Record<…>` makes a new `SourceState` a compile
 * error in this file instead.
 *
 * Two maps rather than twelve messages, because the sentence is "{source} could not be read" and
 * composing it from a state and a source name is what stops the same explanation being written three
 * times and drifting twice. §15's "compose several messages" for the same reason.
 */
export const SOURCE_LABEL_KEY = {
  approvals: 'notifications.source.approvals',
  remediation: 'notifications.source.remediation',
  incidents: 'notifications.source.incidents',
  review: 'notifications.source.review',
} satisfies Record<NotificationSource, string>;

export const SOURCE_STATE_KEY = {
  reading: 'notifications.blind.reading',
  /* Never rendered — `blindSpots` filters it out unless a count is attached, and then the count's own
   * sentence is what is shown. Mapped anyway so the record is total. */
  ok: 'notifications.blind.ok',
  unreadable: 'notifications.blind.unreadable',
  forbidden: 'notifications.blind.forbidden',
} satisfies Record<SourceState, string>;

/* ---------------------------------------------------------------- age, for a reader */

/** Whole minutes since an instant, or `null` when there is no answer to give.
 *
 * `null` covers two cases that must not become "0 min": an unparseable timestamp, and a clock skew
 * that puts the instant in the future. "Just now" would be a claim, and this has none to make —
 * `ageInMinutes` in `features/incidents` refuses to invent one for the same reason and this must not
 * undo that.
 *
 * **A third spelling of a three-line subtraction, and it is recorded rather than defended.**
 * `ageInMinutes` takes an `Incident` and `activityWhen` takes a chain entry; neither takes a bare
 * instant, which is what three of these rows carry. Unifying the three means moving a function into
 * `shared/` and touching two features' tests, which is not a change a header component should carry.
 * The *bucketing* — where the judgement is — is shared: `describeAge` is imported rather than copied.
 */
export function minutesSince(at: string, now: Date): number | null {
  const then = Date.parse(at);
  if (Number.isNaN(then)) return null;
  const minutes = Math.floor((now.getTime() - then) / 60_000);
  return minutes < 0 ? null : minutes;
}

export function badgeFor(count: number, reports: readonly SourceReport[]): Badge {
  if (reports.some((report) => report.state === 'reading')) return { kind: 'reading' };
  const partial = isPartial(reports);
  /* The only path to no badge: every source answered, none was withheld from us for a reason we
     could not see, and the answer was zero. */
  if (count === 0 && !partial) return { kind: 'none' };
  return { kind: 'count', count, partial };
}
